import React from "react";
import { Plus } from "lucide-react";
import { beverages } from "../data/mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";

function parsePrice(str) {
  const n = parseFloat(String(str).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/* Small pill button used on featured drinks (big card layout). */
function AddPillBig({ item }) {
  const { add } = useCart();
  const { toast } = useToast();
  if (!item.lookup_key) return null;
  return (
    <button
      onClick={() => {
        add({
          lookup_key: item.lookup_key,
          name: item.name,
          price: parsePrice(item.price),
          image: item.image,
        });
        toast({ title: "Added to order", description: item.name });
      }}
      data-testid={`add-drink-${item.lookup_key}`}
      className="mt-5 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase text-[var(--mm-cream)] border border-[var(--mm-line)] px-4 py-2 hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] hover:border-[var(--mm-accent)] transition-all duration-300"
    >
      <Plus size={14} /> Add to Order
    </button>
  );
}

/* Tiny inline plus for the compact price-list rows. */
function AddPillTiny({ item }) {
  const { add } = useCart();
  const { toast } = useToast();
  if (!item.lookup_key) return null;
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        add({
          lookup_key: item.lookup_key,
          name: item.name,
          price: parsePrice(item.price),
          image: null,
        });
        toast({ title: "Added to order", description: item.name });
      }}
      data-testid={`add-drink-${item.lookup_key}`}
      aria-label={`Add ${item.name}`}
      className="shrink-0 w-7 h-7 flex items-center justify-center text-[var(--mm-accent)] border border-[var(--mm-line)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] hover:border-[var(--mm-accent)] transition-all"
    >
      <Plus size={14} />
    </button>
  );
}

export default function Beverages() {
  return (
    <section id="beverages" className="py-24 md:py-32 border-t border-[var(--mm-line)]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="text-center mb-16 fade-in">
          <div className="section-label mb-4">Drinks</div>
          <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
            The <span className="text-[var(--mm-accent)]">Bar.</span>
          </h2>
          <p className="text-[var(--mm-cream-muted)] mt-6 max-w-xl mx-auto font-light">
            Homemade, tropical and classic — poured cold and made to cut the heat.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 border border-[var(--mm-accent)]/50 bg-[var(--mm-accent)]/8 px-4 py-2 text-[0.68rem] tracking-[0.24em] uppercase text-[var(--mm-accent)]">
            <Plus size={13} /> Combo Deal · Any Meal + Drink saves $2 — applied automatically at checkout
          </div>
        </div>

        {/* Featured drinks with photos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {beverages.featured.map((drink) => (
            <article key={drink.name} className="fade-in group">
              <div className="img-hover aspect-[4/5] overflow-hidden mb-5">
                <img src={drink.image} alt={drink.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex justify-between items-start gap-4">
                <h3 className="font-display text-2xl italic font-light text-[var(--mm-cream)] leading-tight">
                  {drink.name}
                </h3>
                <div className="font-display text-xl text-[var(--mm-accent)] shrink-0">{drink.price}</div>
              </div>
              <p className="text-sm text-[var(--mm-cream-muted)] mt-3 leading-relaxed font-light">
                {drink.description}
              </p>
              <AddPillBig item={drink} />
            </article>
          ))}
        </div>

        {/* Grouped list */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14 fade-in">
          {beverages.groups.map((group) => (
            <div key={group.title}>
              <div className="section-label mb-5">{group.title}</div>
              <ul className="space-y-3">
                {group.items.map((item) => (
                  <li
                    key={item.name}
                    className="flex items-baseline gap-3 pb-2 border-b border-[var(--mm-line)]"
                  >
                    <span className="font-display italic text-lg text-[var(--mm-cream)] leading-tight">
                      {item.name}
                    </span>
                    <span className="flex-1 border-b border-dotted border-[var(--mm-line)] translate-y-[-3px]" />
                    <span className="font-display text-[var(--mm-accent)]">{item.price}</span>
                    <AddPillTiny item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
